import { FC } from "react";
import styled from "@emotion/styled";
import { MDSLogoKind, MDSLogoSize } from "./logo.types";

import LogoRoundedSvg from "@/assets/icons/logo/logo-rounded.svg?react";
import LogoSquareSvg from "@/assets/icons/logo/logo-square.svg?react";
import ArrowsCroppedSvg from "@/assets/icons/logo/arrows-cropped.svg?react";
import LockupSvg from "@/assets/icons/logo/lockup.svg?react";
import WordmarkSvg from "@/assets/icons/logo/wordmark.svg?react";
import LogoCircleSvg from "@/assets/icons/logo/logo-circle.svg?react";
import ArrowsSvg from "@/assets/icons/logo/arrows.svg?react";

export interface MDSLogoProps {
  kind: MDSLogoKind;
  size?: MDSLogoSize;
  className?: string;
}

export const getLogoSize = (kind: MDSLogoKind, size: MDSLogoSize): number => {
  switch (kind) {
    case MDSLogoKind.LogoCircle:
    case MDSLogoKind.LogoRounded:
    case MDSLogoKind.LogoSquare:
      switch (size) {
        case MDSLogoSize.Small:
          return 24;
        case MDSLogoSize.Medium:
          return 56;
        case MDSLogoSize.Large:
          return 88;
        default:
          return 24;
      }
    case MDSLogoKind.Arrows:
      switch (size) {
        case MDSLogoSize.Small:
          return 16;
        case MDSLogoSize.Medium:
          return 24;
        case MDSLogoSize.Large:
          return 32;
        default:
          return 24;
      }
    case MDSLogoKind.ArrowsCropped:
    case MDSLogoKind.Lockup:
    case MDSLogoKind.Wordmark:
    default:
      switch (size) {
        case MDSLogoSize.Small:
          return 12;
        case MDSLogoSize.Medium:
          return 16;
        case MDSLogoSize.Large:
          return 24;
        default:
          return 16;
      }
  }
};

const StyledLogoWrapper = styled.div<{ $height: number; $isCustomTextColor?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: ${({ $height }) => $height}px;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;

    path:last-child {
      fill: ${({ theme, $isCustomTextColor }) =>
        $isCustomTextColor ? theme.colors.text.contrast : "auto"};
    }
  }
`;

export const MDSLogo: FC<MDSLogoProps> = ({ kind, size = MDSLogoSize.Medium, className }) => {
  const height = getLogoSize(kind, size);
  const $isCustomTextColor = [MDSLogoKind.Wordmark, MDSLogoKind.Lockup].includes(kind);

  const renderLogo = () => {
    switch (kind) {
      case MDSLogoKind.LogoCircle:
        return <LogoCircleSvg />;
      case MDSLogoKind.LogoRounded:
        return <LogoRoundedSvg />;
      case MDSLogoKind.LogoSquare:
        return <LogoSquareSvg />;
      case MDSLogoKind.Lockup:
        return <LockupSvg />;
      case MDSLogoKind.Wordmark:
        return <WordmarkSvg />;
      case MDSLogoKind.ArrowsCropped:
        return <ArrowsCroppedSvg />;
      case MDSLogoKind.Arrows:
        return <ArrowsSvg />;
      default:
        return <LogoRoundedSvg />;
    }
  };

  return (
    <StyledLogoWrapper
      $height={height}
      className={className}
      $isCustomTextColor={$isCustomTextColor}
    >
      {renderLogo()}
    </StyledLogoWrapper>
  );
};
